import { useMemo, useState, useRef, useEffect } from "react";
import { endOfMonth, format } from "date-fns";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
} from "recharts";
import type { DateRange } from "react-day-picker";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { DateRangePicker } from "../../ui/date-range-picker";
import { FilterDropdown, type FilterOption } from "../../ui/filter-dropdown";
import { ExportMenu } from "../../ui/export-menu";
import { X } from "lucide-react";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { useChartColors } from "../../../hooks/useChartColors";
import { useRevenueBreakdown } from "../hooks/useRevenueBreakdown";
import DataLoader from "../../../components/loaders/DataLoader";
import { MonthRangePicker } from "../../ui/month-year-range";
import { formatCurrency } from "../../../utils/formatCurrency";

export const channelOptions: FilterOption[] = [
  { label: "All Channels", value: "all_channels" },
  { label: "Shopify", value: "shopify" },
  { label: "Wholesale", value: "wholesale" },
];

export const comparisonOptions: FilterOption[] = [
  { label: "Revenue & Profit", value: "profit" },
  { label: "Revenue Only", value: "no_comparison" },
];

export const granularityOptions: FilterOption[] = [
  { label: "Daily", value: "daily" },
  { label: "Monthly", value: "monthly" },
];

export function RevenueChart() {
  const now = new Date();
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: now,
  });
  const [comparison, setComparison] = useState<string>("profit");
  const [granularity, setGranularity] = useState<string>("daily");
  const [channel, setChannel] = useState<string>("all_channels");
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(400); // Default height



  const params = useMemo(() => {
    const rawStart = dateRange?.from;
    const rawEnd = dateRange?.to;

    const start =
      granularity === "monthly" && rawStart
        ? new Date(rawStart.getFullYear(), rawStart.getMonth(), 1)
        : rawStart;

    const end =
      granularity === "monthly" && rawEnd
        ? new Date(rawEnd.getFullYear(), rawEnd.getMonth() + 1, 0)
        : rawEnd;

    return {
      start_date: start ? format(start, "yyyy-MM-dd") : "",
      end_date: end ? format(end, "yyyy-MM-dd") : "",
      channel,
      kind: comparison,
      period_type: granularity,
    };
  }, [dateRange, channel, comparison, granularity]);
  useEffect(() => {
    if (
      granularity === "monthly" &&
      dateRange?.from &&
      dateRange?.to
    ) {
      const fromMonthStart = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1);
      const toMonthEnd = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth() + 1, 0);

      const isSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() &&
        dateRange.from.getFullYear() === dateRange.to.getFullYear();

      const isOneMonthOnly =
        fromMonthStart.getTime() === dateRange.from.getTime() &&
        toMonthEnd.getTime() === dateRange.to.getTime() &&
        isSameMonth;

      if (isOneMonthOnly) {
        const start = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1);
        const end = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth() + 2, 0); // end of *next* month
        setDateRange({ from: start, to: end });
      }
    }
    if (granularity === "daily") {
      // Reset to current month for daily
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDateRange({ from: start, to: end });
    }
  }, [granularity]);
  const colors = useChartColors("overview");

  const { data, error, isLoading } = useRevenueBreakdown(params);


  // Use ResizeObserver to dynamically adjust height
  useEffect(() => {
    if (chartContainerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setContainerHeight(entry.contentRect.height);
        }
      });
      resizeObserver.observe(chartContainerRef.current);
      return () => resizeObserver.disconnect(); // Cleanup
    }
  }, []);
  if (!colors) return null;

  if (error) return <div>Error loading revenue data.</div>;

  const processedData = applyGranularity(data ?? [], granularity);
  const resetFilters = () => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setDateRange({ from: firstDayOfMonth, to: lastDayOfMonth });
    setComparison("profit");
    setGranularity("daily");
    setChannel("all_channels");
  };

  const allYValues = processedData.flatMap((d) => [
    d.revenue_value,
    comparison === "profit" ? d.profit_value : null,
    comparison === "last_period" ? d.previous_revenue : null,
    comparison === "last_year" ? d.last_year : null,
  ]).filter((v): v is number => typeof v === "number");

  const minY = Math.min(...allYValues);
  const maxY = Math.max(...allYValues);
  const yMin = minY > 0 ? 0 : minY * 1.1;
  const yMax = maxY * 1.1;

  const smartFormatter = (value: number) => {
    if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return `$${value}`;
  };

  return (
    <>
      <SummaryHeader title="Revenue Breakdown" />
      <Card className="flex flex-col h-full">
        <CardHeader>
          <div className="flex flex-wrap gap-2 w-full items-start">
            <div className="min-w-[180px] max-w-full">
              {granularity === "monthly" ? (
                <MonthRangePicker
                  value={{ start: dateRange?.from, end: dateRange?.to }}
                  onChange={({ start, end }) => {
                    setDateRange({ from: start, to: end });
                  }}
                  className="w-full"
                />
              ) : (
                <DateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  className="w-full"
                />
              )}
            </div>

            <div className="min-w-[150px] max-w-full">
              <FilterDropdown
                options={comparisonOptions}
                value={comparison}
                onChange={setComparison}
                placeholder="Comparison"
                triggerClassName="w-full"
              />
            </div>

            <div className="min-w-[150px] max-w-full">
              <FilterDropdown
                options={granularityOptions}
                value={granularity}
                onChange={setGranularity}
                placeholder="Granularity"
                triggerClassName="w-full"
              />
            </div>

            <div className="min-w-[150px] max-w-full">
              <FilterDropdown
                options={channelOptions}
                value={channel}
                onChange={setChannel}
                placeholder="Sales Channel"
                triggerClassName="w-full"
              />
            </div>

            <div className="min-w-[100px] max-w-full">
              <Button size="sm" onClick={resetFilters} className="w-full">
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            </div>

            <div className="min-w-[100px] max-w-full">
              <ExportMenu
                data={processedData}
                filename="revenue_data"
                buttonSize="sm"
              />
            </div>
          </div>
        </CardHeader>





        {isLoading ? (
          <DataLoader variant="revenue-chart" itemCount={1} />
        ) : (
          <CardContent className="flex-grow" ref={chartContainerRef}>
            <div style={{ height: `${containerHeight}px` }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={processedData}
                  margin={{ top: 10, right: 10, left: 4, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke="var(--chart-grid)"
                    vertical={false}
                    strokeDasharray="4 4"
                    strokeWidth="1"
                  />
                  <XAxis
                    dataKey="date"
                    className="text-xs sm:text-sm text-muted-foreground"
                    tickLine={false}
                    axisLine={true}
                    tick={{ fontSize: 12 }}
                    tickMargin={8}
                    tickFormatter={(date) =>
                      granularity === "monthly"
                        ? format(new Date(date), "MMM yyyy")
                        : format(new Date(date), "MMM d")
                    }
                  />
                  <YAxis
                    className="text-xs sm:text-sm text-muted-foreground"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={smartFormatter}
                    width={60}
                    domain={[yMin, yMax]}
                  />
                  <Tooltip
                    labelFormatter={(label) =>
                      granularity === "monthly"
                        ? format(new Date(label), "MMM yyyy")
                        : format(new Date(label), "MMM d yyyy")
                    }
                    formatter={(value) => formatCurrency(Number(value))}
                    contentStyle={{
                      backgroundColor: "var(--background)",
                      borderColor: "var(--border)",
                      borderRadius: "var(--radius)",
                      fontSize: "0.875rem",
                    }}
                    itemStyle={{ color: "var(--chart-text)" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue_value"
                    name="Revenue"
                    stroke={colors[3]}
                    fill={colors[3]}
                    fillOpacity={0.2}
                    activeDot={{ r: 6 }}
                    strokeWidth={2}
                  />
                  {comparison === "profit" && (
                    <Area
                      type="monotone"
                      dataKey="profit_value"
                      name="Profit"
                      stroke={colors[2]}
                      fill={colors[2]}
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                  )}
                  <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{
                      paddingBottom: 20,
                      marginRight: -32,
                      marginTop: -24,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        )}
      </Card>
    </>
  );
}

function applyGranularity(data: any[], granularity: string) {
  if (granularity === "daily") return data;

  if (granularity === "monthly") {
    const monthlyData: any[] = [];
    const months: Record<string, any> = {};

    data.forEach((item) => {
      const date = new Date(item.date);
      const monthKey = format(date, "MMM yyyy");

      if (!months[monthKey]) {
        months[monthKey] = {
          date: endOfMonth(date).toISOString(),
          revenue_value: 0,
          profit_value: 0,
          previous_revenue: 0,
          last_year: 0,
          count: 0,
        };
      }

      months[monthKey].revenue_value += item.revenue_value || 0;
      months[monthKey].profit_value += item.profit_value || 0;
      months[monthKey].previous_revenue += item.previous_revenue || 0;
      months[monthKey].last_year += item.last_year || 0;
      months[monthKey].count += 1;
    });

    Object.values(months).forEach((month) => {
      monthlyData.push({
        date: month.date,
        revenue_value: Math.round(month.revenue_value),
        profit_value: Math.round(month.profit_value),
        previous_revenue: Math.round(month.previous_revenue),
        last_year: Math.round(month.last_year),
      });
    });

    return monthlyData;
  }
  return data;
}